/**
 * Phase 2 domain vocabulary: retailer onboarding, locations, hours, catalog,
 * listings, inventory and imports. These literal unions are the single source of
 * truth shared by the API, the services and the SQL CHECK constraints. When a
 * value is added here it must be added to the matching migration constraint, and
 * the migration tests assert the two agree.
 */

// -- Retailer onboarding ----------------------------------------------------

export const RETAILER_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'INFO_REQUESTED',
  'APPROVED',
  'REJECTED',
  'SUSPENDED',
  'CLOSED',
] as const;
export type RetailerStatus = (typeof RETAILER_STATUSES)[number];

export const APPLICATION_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'INFO_REQUESTED',
  'APPROVED',
  'REJECTED',
  'WITHDRAWN',
] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

/** Legal transitions for a retailer application. Anything absent is rejected. */
export const APPLICATION_TRANSITIONS: Record<ApplicationStatus, readonly ApplicationStatus[]> = {
  DRAFT: ['SUBMITTED', 'WITHDRAWN'],
  SUBMITTED: ['UNDER_REVIEW', 'INFO_REQUESTED', 'APPROVED', 'REJECTED', 'WITHDRAWN'],
  UNDER_REVIEW: ['INFO_REQUESTED', 'APPROVED', 'REJECTED'],
  INFO_REQUESTED: ['SUBMITTED', 'UNDER_REVIEW', 'REJECTED', 'WITHDRAWN'],
  APPROVED: [],
  REJECTED: [],
  WITHDRAWN: [],
};

export const LOCATION_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'INFO_REQUESTED',
  'APPROVED',
  'REJECTED',
  'SUSPENDED',
  'CLOSED',
] as const;
export type LocationStatus = (typeof LOCATION_STATUSES)[number];

export const DOCUMENT_TYPES = [
  'BUSINESS_LICENSE',
  'RESALE_CERTIFICATE',
  'GOVERNMENT_ID',
  'INSURANCE',
  'BANK_VERIFICATION',
  'ADDRESS_PROOF',
  'OTHER',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const DOCUMENT_STATUSES = ['REQUESTED', 'PROVIDED', 'ACCEPTED', 'REJECTED'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const SCAN_STATUSES = ['PENDING', 'CLEAN', 'INFECTED', 'ERROR', 'SKIPPED'] as const;
export type ScanStatus = (typeof SCAN_STATUSES)[number];

/**
 * Payout onboarding states permitted in Phase 2. No live value exists: reaching
 * a live payout provider requires a later phase and founder approval.
 */
export const PAYOUT_ONBOARDING_STATES = [
  'INTEGRATION_ACCOUNT_REQUIRED',
  'SANDBOX_PENDING',
  'SANDBOX_COMPLETE',
  'NOT_APPLICABLE',
] as const;
export type PayoutOnboardingState = (typeof PAYOUT_ONBOARDING_STATES)[number];

export const BUSINESS_STRUCTURES = [
  'SOLE_PROPRIETOR',
  'LLC',
  'CORPORATION',
  'PARTNERSHIP',
  'NONPROFIT',
  'OTHER',
] as const;
export type BusinessStructure = (typeof BUSINESS_STRUCTURES)[number];

export const APPLICATION_EVENT_TYPES = [
  'CREATED',
  'DRAFT_SAVED',
  'SUBMITTED',
  'REVIEW_STARTED',
  'INFO_REQUESTED',
  'INFO_PROVIDED',
  'DOCUMENT_UPLOADED',
  'DOCUMENT_REVIEWED',
  'AGREEMENT_ACCEPTED',
  'LOCATION_SUBMITTED',
  'APPROVED',
  'REJECTED',
  'WITHDRAWN',
  'SUSPENDED',
  'REINSTATED',
] as const;
export type ApplicationEventType = (typeof APPLICATION_EVENT_TYPES)[number];

// -- Hours and availability -------------------------------------------------

/**
 * Public store hours, Beauty Burst ordering hours and Beauty Burst fulfillment
 * hours are separate schedules on purpose: a store can be open to walk-ins while
 * closed to Beauty Burst orders, and can accept orders it cannot yet fulfil.
 */
export const SCHEDULE_KINDS = [
  'PUBLIC_STORE',
  'BB_ORDERING',
  'BB_FULFILLMENT',
  'DELIVERY',
  'PICKUP',
] as const;
export type ScheduleKind = (typeof SCHEDULE_KINDS)[number];

export const SCHEDULE_MODES = ['CLOSED', 'SCHEDULED', 'ALWAYS_OPEN'] as const;
export type ScheduleMode = (typeof SCHEDULE_MODES)[number];

export const WINDOW_KINDS = ['STANDARD', 'LATE_NIGHT', 'OVERNIGHT'] as const;
export type WindowKind = (typeof WINDOW_KINDS)[number];

export const MINUTES_PER_DAY = 1440;

export const OVERNIGHT_APPROVAL_STATUSES = [
  'NOT_REQUESTED',
  'PENDING',
  'APPROVED',
  'REJECTED',
  'REVOKED',
] as const;
export type OvernightApprovalStatus = (typeof OVERNIGHT_APPROVAL_STATUSES)[number];

export const OVERNIGHT_REQUEST_KINDS = ['LATE_NIGHT', 'OVERNIGHT'] as const;
export type OvernightRequestKind = (typeof OVERNIGHT_REQUEST_KINDS)[number];

// -- Catalog ---------------------------------------------------------------

export const PRODUCT_STATUSES = ['DRAFT', 'ACTIVE', 'ARCHIVED', 'BLOCKED'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

export const PRODUCT_SOURCES = ['PLATFORM', 'RETAILER_SUBMITTED', 'IMPORT'] as const;
export type ProductSource = (typeof PRODUCT_SOURCES)[number];

/**
 * Beauty-specific variant attributes. A product uses only the attributes that
 * apply to it: lashes carry LENGTH and STYLE, foundation carries SHADE.
 */
export const VARIANT_ATTRIBUTE_KEYS = [
  'COLOR',
  'SHADE',
  'LENGTH',
  'TEXTURE',
  'SIZE',
  'STYLE',
  'PACK_QUANTITY',
  'FINISH',
  'SCENT',
  'VOLUME',
  'WEIGHT',
  'SPF',
  'DENSITY',
  'CAP_SIZE',
  'ORIGIN',
] as const;
export type VariantAttributeKey = (typeof VARIANT_ATTRIBUTE_KEYS)[number];

export const GTIN_TYPES = ['UPC_A', 'UPC_E', 'EAN_8', 'EAN_13', 'GTIN_14'] as const;
export type GtinType = (typeof GTIN_TYPES)[number];

// -- Seller listings -------------------------------------------------------

/**
 * Listing ownership. Beauty Burst Direct stock and revenue belong to Beauty
 * Burst and must never be reported, paid out or displayed as retailer-owned.
 */
export const OWNERSHIP_TYPES = ['RETAILER', 'BEAUTY_BURST_DIRECT'] as const;
export type OwnershipType = (typeof OWNERSHIP_TYPES)[number];

export const LISTING_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'ARCHIVED', 'BLOCKED'] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];

export const ELIGIBILITY_RULE_KINDS = [
  'PICKUP',
  'DELIVERY',
  'OVERNIGHT',
  'LATE_NIGHT',
  'AGE_RESTRICTED',
  'HAZMAT',
  'MARKETPLACE_BLOCKED',
] as const;
export type EligibilityRuleKind = (typeof ELIGIBILITY_RULE_KINDS)[number];

export const ELIGIBILITY_SCOPE_KINDS = [
  'GLOBAL',
  'CATEGORY',
  'BRAND',
  'PRODUCT',
  'VARIANT',
  'LISTING',
] as const;
export type EligibilityScopeKind = (typeof ELIGIBILITY_SCOPE_KINDS)[number];

// -- Inventory -------------------------------------------------------------

export const INVENTORY_STATUSES = ['IN_STOCK', 'LOW', 'CRITICAL', 'OUT'] as const;
export type InventoryStatus = (typeof INVENTORY_STATUSES)[number];

export const RESERVATION_STATUSES = ['ACTIVE', 'RELEASED', 'COMMITTED', 'EXPIRED'] as const;
export type ReservationStatus = (typeof RESERVATION_STATUSES)[number];

export const RESERVATION_REASONS = ['CART', 'CHECKOUT', 'ORDER', 'MANUAL_HOLD'] as const;
export type ReservationReason = (typeof RESERVATION_REASONS)[number];

/**
 * Why a reservation was released. PAYMENT_FAILED and STORE_DECLINED exist now so
 * Phase 4 can attach payment and store-decision events without a schema change.
 */
export const RELEASE_REASONS = [
  'EXPIRED',
  'CANCELLED',
  'STORE_DECLINED',
  'PAYMENT_FAILED',
  'MANUAL_RELEASE',
  'SUPERSEDED',
  'MANUAL_ADJUSTMENT',
] as const;
export type ReleaseReason = (typeof RELEASE_REASONS)[number];

export const MOVEMENT_TYPES = [
  'RECEIVE',
  'MANUAL_ADJUSTMENT',
  'RESERVATION_CREATED',
  'RESERVATION_RELEASED',
  'SALE_COMMITTED',
  'RETURN_TO_STOCK',
  'CYCLE_COUNT_ADJUSTMENT',
  'IMPORT_ADJUSTMENT',
] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

// -- Imports ---------------------------------------------------------------

/** The approved six-step import workflow, in order. */
export const IMPORT_STEPS = ['UPLOAD', 'MAP', 'VALIDATE', 'FIX', 'PREVIEW', 'PUBLISH'] as const;
export type ImportStep = (typeof IMPORT_STEPS)[number];

export const IMPORT_STATUSES = [
  'UPLOADED',
  'MAPPING',
  'VALIDATING',
  'VALIDATED',
  'FIXING',
  'PREVIEWED',
  'PUBLISHING',
  'PUBLISHED',
  'FAILED',
  'CANCELLED',
] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

export const IMPORT_FILE_FORMATS = ['CSV', 'XLSX'] as const;
export type ImportFileFormat = (typeof IMPORT_FILE_FORMATS)[number];

export const IMPORT_ROW_ACTIONS = ['PENDING', 'CREATE', 'UPDATE', 'SKIP', 'ERROR'] as const;
export type ImportRowAction = (typeof IMPORT_ROW_ACTIONS)[number];

export const IMPORT_ROW_STATUSES = [
  'PENDING',
  'VALID',
  'WARNING',
  'ERROR',
  'RESOLVED',
  'PUBLISHED',
  'FAILED',
] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

/**
 * Safe update matching. Precedence is the array order: the most specific and
 * least ambiguous identifier wins. AMBIGUOUS is a terminal outcome that requires
 * a human decision; ambiguous rows are never merged silently.
 */
export const MATCH_STRATEGIES = [
  'LISTING_ID',
  'SELLER_SKU',
  'EXTERNAL_REF',
  'UPC',
  'NONE',
  'AMBIGUOUS',
] as const;
export type MatchStrategy = (typeof MATCH_STRATEGIES)[number];

/** Ordered precedence used by the matcher. Earlier identifiers win outright. */
export const MATCH_PRECEDENCE: readonly MatchStrategy[] = [
  'LISTING_ID',
  'SELLER_SKU',
  'EXTERNAL_REF',
  'UPC',
];

/** Canonical Beauty Burst import fields a source column may be mapped to. */
export const IMPORT_TARGET_FIELDS = [
  'listingId',
  'sellerSku',
  'externalRef',
  'upc',
  'productName',
  'brand',
  'category',
  'variantName',
  'color',
  'shade',
  'length',
  'texture',
  'size',
  'style',
  'packQuantity',
  'price',
  'salePrice',
  'onHand',
  'lowStockThreshold',
  'criticalStockThreshold',
  'overnightEligible',
  'pickupEligible',
  'deliveryEligible',
  'listingStatus',
] as const;
export type ImportTargetField = (typeof IMPORT_TARGET_FIELDS)[number];

export const IMPORT_TRANSFORMS = [
  'TRIM',
  'UPPER',
  'LOWER',
  'CENTS_FROM_DECIMAL',
  'INTEGER',
  'BOOLEAN',
] as const;
export type ImportTransform = (typeof IMPORT_TRANSFORMS)[number];

export const STORAGE_DRIVERS = ['LOCAL_TEMP', 'MEMORY'] as const;
export type StorageDriver = (typeof STORAGE_DRIVERS)[number];
